/**
 * Orchestration MCP Tools
 *
 * Moves mechanical Type B logic out of the foreman otter's system prompt
 * into tested TypeScript. Reduces foreman prompt from ~2,750 words to ~600 words,
 * enabling reliable operation on 7B parameter models for airgapped deployments.
 *
 * These tools replace pseudocode that was previously embedded in the foreman
 * prompt as inline helper functions and procedural instructions.
 */
import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';
import { jsonCoerce } from '../coerce.js';
import { readFileSync, writeFileSync, existsSync, mkdirSync, lstatSync } from 'fs';
import { join } from 'path';
import {
  parseLLMQuestionsResponse,
  answersToManifestFormat,
  type QuestionManifestQuestion,
} from '../question-adapter.js';
import { isValidPhase } from '../validation.js';

// ---------------------------------------------------------------------------
// Constants — version-controlled, not buried in a 2,750-word prompt
// ---------------------------------------------------------------------------

/**
 * Maps otter name substrings → canonical phase name.
 * Order matters: more-specific entries first.
 */
const OTTER_NAME_TO_PHASE: Array<[string, string]> = [
  ['designer', 'designer'],
  ['theme', 'theme'],
  ['scaffold', 'scaffold'],
  ['api', 'api'],
  ['auth', 'auth'],
  ['dashboard', 'dashboard'],
  ['data', 'data'],
  ['page', 'pages'],
  // swp-4071: workflow-otter renamed to form-wizard-otter; keep 'workflow' for
  // any in-flight session that still uses the old name (belt-and-suspenders)
  ['form-wizard', 'workflow'],
  ['workflow', 'workflow'],
  ['services', 'services'],
  ['polish', 'polish'],
  ['geo', 'geo'],
];

const PHASE_TO_OTTER: Record<string, string> = {
  designer: 'stackwright-pro-designer-otter',
  theme: 'stackwright-pro-theme-otter',
  scaffold: 'stackwright-pro-scaffold-otter',
  api: 'stackwright-pro-api-otter',
  auth: 'stackwright-pro-auth-otter',
  pages: 'stackwright-pro-page-otter',
  dashboard: 'stackwright-pro-dashboard-otter',
  data: 'stackwright-pro-data-otter',
  workflow: 'stackwright-pro-form-wizard-otter',
  services: 'stackwright-services-otter',
  polish: 'stackwright-pro-polish-otter',
  geo: 'stackwright-pro-geo-otter',
};

// ---------------------------------------------------------------------------
// Phase detection
// ---------------------------------------------------------------------------

function detectPhase(otterName: string): string {
  const lower = otterName.toLowerCase();
  for (const [keyword, phase] of OTTER_NAME_TO_PHASE) {
    if (lower.includes(keyword)) return phase;
  }
  // Fallback: strip well-known wrapper and return whatever's left
  return lower.replace(/^stackwright-pro-/, '').replace(/-otter$/, '');
}

// ---------------------------------------------------------------------------
// Handler functions (exported for direct testing — no MCP server needed)
// ---------------------------------------------------------------------------

export interface ParseOtterResponseInput {
  otterName: string;
  responseText: string;
}

export interface ParseOtterResponseResult {
  phase: string;
  otter: string;
  questions: QuestionManifestQuestion[];
  error?: true;
  parseError?: string;
}

export function handleParseOtterResponse(input: ParseOtterResponseInput): {
  result: ParseOtterResponseResult;
  isError: boolean;
} {
  const phase = detectPhase(input.otterName);
  try {
    const questions = parseLLMQuestionsResponse(input.responseText);
    return {
      result: { phase, otter: input.otterName, questions },
      isError: false,
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return {
      result: {
        error: true,
        otterName: input.otterName,
        phase,
        questions: [],
        parseError: message,
      } as unknown as ParseOtterResponseResult,
      isError: true,
    };
  }
}

// ---------------------------------------------------------------------------

export interface PhaseEntry {
  phase: string;
  otter: string;
  questions: unknown[];
  requiredPackages?: {
    dependencies?: Record<string, string>;
    devPackages?: Record<string, string>;
  };
}

export interface SaveManifestInput {
  phases: PhaseEntry[];
  _cwd?: string; // injectable for tests
}

export function handleSaveManifest(input: SaveManifestInput): { text: string; isError: boolean } {
  const cwd = input._cwd ?? process.cwd();
  const dir = join(cwd, '.stackwright');
  const filePath = join(dir, 'question-manifest.json');
  try {
    mkdirSync(dir, { recursive: true });

    // Symlink guard — refuse to write through a symlink
    if (existsSync(filePath)) {
      const stat = lstatSync(filePath);
      if (stat.isSymbolicLink()) {
        const message = `Refusing to write to symlink: ${filePath}`;
        return {
          text: JSON.stringify({ success: false, error: message }),
          isError: true,
        };
      }
    }

    const manifest = {
      version: '1.0',
      createdAt: new Date().toISOString(),
      phases: input.phases,
    };
    writeFileSync(filePath, JSON.stringify(manifest, null, 2) + '\n');
    return {
      text: JSON.stringify({ success: true, path: filePath, phaseCount: input.phases.length }),
      isError: false,
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return {
      text: JSON.stringify({ success: false, error: message }),
      isError: true,
    };
  }
}

// ---------------------------------------------------------------------------

export interface RawAnswer {
  question_header: string;
  selected_options: string[];
  other_text?: string | null;
}

export interface SavePhaseAnswersInput {
  phase: string;
  rawAnswers: RawAnswer[];
  questions?: QuestionManifestQuestion[];
  _cwd?: string; // injectable for tests
}

export function handleSavePhaseAnswers(input: SavePhaseAnswersInput): {
  text: string;
  isError: boolean;
} {
  // ── Phase name validation (CWE-22 path traversal guard) ──────────────
  if (!isValidPhase(input.phase)) {
    return {
      text: JSON.stringify({
        success: false,
        error: `Invalid phase name: "${input.phase.slice(0, 50)}". Must be lowercase alphanumeric with hyphens, max 31 chars.`,
      }),
      isError: true,
    };
  }

  // ── Per-item rawAnswers validation ───────────────────────────────────
  for (let i = 0; i < input.rawAnswers.length; i++) {
    const a = input.rawAnswers[i]!;
    if (!a.question_header || a.question_header.trim().length === 0) {
      return {
        text: JSON.stringify({
          success: false,
          error: `rawAnswers[${i}].question_header is empty — every answer must have a non-empty question_header.`,
        }),
        isError: true,
      };
    }
    if (!a.selected_options || a.selected_options.length === 0) {
      return {
        text: JSON.stringify({
          success: false,
          error: `rawAnswers[${i}].selected_options is empty for question_header "${a.question_header}" — every answer must have at least one selected option.`,
        }),
        isError: true,
      };
    }
  }

  // ── Cross-validation: questions provided but rawAnswers empty ────────
  if (input.questions && input.questions.length > 0 && input.rawAnswers.length === 0) {
    return {
      text: JSON.stringify({
        success: false,
        error: `${input.questions.length} questions provided but rawAnswers is empty — this is a malformed payload. If the phase truly has no questions, omit the questions parameter.`,
      }),
      isError: true,
    };
  }

  const cwd = input._cwd ?? process.cwd();
  const dir = join(cwd, '.stackwright', 'answers');
  const filePath = join(dir, `${input.phase}.json`);
  try {
    mkdirSync(dir, { recursive: true });

    const warnings: string[] = [];
    let answers: Record<string, unknown>;

    if (input.questions && input.questions.length > 0) {
      try {
        answers = answersToManifestFormat(input.rawAnswers, input.questions);
      } catch (mapErr) {
        // Questions passed in display format (e.g. {header, multi_select, options: [{label, description}]})
        // instead of manifest format (e.g. {id, type, options: [{label, value}]}) — the domain-expert
        // otter sometimes forwards QUESTIONS verbatim from its invocation prompt.
        // Fall through to direct key mapping rather than erroring.
        answers = {};
        warnings.push(
          `Reverse-mapping threw (${
            mapErr instanceof Error ? mapErr.message : String(mapErr)
          }) — falling back to direct key mapping.`
        );
      }
      // Domain-expert fallback: reverse-mapping silently produces {} when the
      // question_header values don't match the generated manifest headers (e.g.
      // domain-expert otter uses "designer-1" instead of "DESI-1", or uses free-text
      // reasoning keys). If the mapping produced nothing despite non-empty rawAnswers,
      // fall back to direct-mapping so answers are never silently dropped.
      if (Object.keys(answers).length === 0 && input.rawAnswers.length > 0) {
        answers = Object.fromEntries(
          input.rawAnswers.map((a) => [
            a.question_header,
            a.selected_options.length > 1 ? a.selected_options : (a.selected_options[0] ?? ''),
          ])
        );
        warnings.push(
          `Reverse-mapping produced empty answers despite ${input.rawAnswers.length} rawAnswers — used direct key mapping as fallback. ` +
            `Ensure question_header values match the manifest header format (e.g. "DESI-1", not descriptive keys).`
        );
      }

      // Completeness check: warn if fewer answers than questions
      const requiredCount = input.questions.filter((q) => q.required !== false).length;
      if (input.rawAnswers.length < requiredCount) {
        warnings.push(
          `Only ${input.rawAnswers.length} answers provided for ${requiredCount} required questions (${input.questions.length} total) — answers may be incomplete.`
        );
      }
    } else {
      // Fallback: no manifest questions provided — best-effort key extraction
      answers = Object.fromEntries(
        input.rawAnswers.map((a) => [a.question_header, a.selected_options[0] ?? ''])
      );
    }

    const payload = {
      version: '1.0',
      phase: input.phase,
      completedAt: new Date().toISOString(),
      answers,
    };

    // Symlink guard — refuse to write through a symlink
    if (existsSync(filePath)) {
      const stat = lstatSync(filePath);
      if (stat.isSymbolicLink()) {
        const message = `Refusing to write to symlink: ${filePath}`;
        return {
          text: JSON.stringify({ success: false, error: message }),
          isError: true,
        };
      }
    }

    writeFileSync(filePath, JSON.stringify(payload, null, 2) + '\n');
    return {
      text: JSON.stringify({
        success: true,
        path: filePath,
        answersCount: Object.keys(answers).length,
        ...(warnings.length > 0 ? { warnings } : {}),
      }),
      isError: false,
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return {
      text: JSON.stringify({ success: false, error: message }),
      isError: true,
    };
  }
}

// ---------------------------------------------------------------------------

export interface ReadPhaseAnswersInput {
  phase: string;
  _cwd?: string; // injectable for tests
}

export function handleReadPhaseAnswers(input: ReadPhaseAnswersInput): {
  text: string;
  isError: boolean;
} {
  const cwd = input._cwd ?? process.cwd();
  const filePath = join(cwd, '.stackwright', 'answers', `${input.phase}.json`);
  if (!existsSync(filePath)) {
    return {
      text: JSON.stringify({ missing: true, phase: input.phase }),
      isError: false,
    };
  }
  try {
    const raw = readFileSync(filePath, 'utf8');
    const parsed = JSON.parse(raw);
    return { text: JSON.stringify(parsed), isError: false };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return {
      text: JSON.stringify({ error: true, phase: input.phase, readError: message }),
      isError: true,
    };
  }
}

// ---------------------------------------------------------------------------

export interface GetOtterNameInput {
  phase: string;
}

export function handleGetOtterName(input: GetOtterNameInput): { text: string; isError: boolean } {
  const normalised = input.phase.toLowerCase().trim();
  const otterName = PHASE_TO_OTTER[normalised];
  if (!otterName) {
    return {
      text: JSON.stringify({ error: true, message: `Unknown phase: ${input.phase}` }),
      isError: true,
    };
  }
  return {
    text: JSON.stringify({ phase: normalised, otterName }),
    isError: false,
  };
}

// ---------------------------------------------------------------------------

export interface SaveBuildContextInput {
  buildContext: string;
  _cwd?: string;
}

export function handleSaveBuildContext(input: SaveBuildContextInput): {
  text: string;
  isError: boolean;
} {
  const cwd = input._cwd ?? process.cwd();
  const dir = join(cwd, '.stackwright');
  const filePath = join(dir, 'build-context.json');
  try {
    mkdirSync(dir, { recursive: true });

    // Symlink guard
    if (existsSync(filePath)) {
      const stat = lstatSync(filePath);
      if (stat.isSymbolicLink()) {
        return {
          text: JSON.stringify({
            success: false,
            error: `Refusing to write to symlink: ${filePath}`,
          }),
          isError: true,
        };
      }
    }

    const payload = {
      version: '1.0',
      savedAt: new Date().toISOString(),
      buildContext: input.buildContext,
    };
    writeFileSync(filePath, JSON.stringify(payload, null, 2) + '\n');
    return {
      text: JSON.stringify({ success: true, path: filePath }),
      isError: false,
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return {
      text: JSON.stringify({ success: false, error: message }),
      isError: true,
    };
  }
}

// ---------------------------------------------------------------------------
// MCP tool registration
// ---------------------------------------------------------------------------

export function registerOrchestrationTools(server: McpServer): void {
  // -------------------------------------------------------------------------
  // 0. stackwright_pro_save_build_context
  // -------------------------------------------------------------------------
  server.tool(
    'stackwright_pro_save_build_context',
    "Save the user's initial build description to .stackwright/build-context.json. " +
      'Call this once at startup after the user answers the opening "what are you building" question. ' +
      'The saved context is automatically prepended to specialist prompts by stackwright_pro_build_specialist_prompt.',
    {
      buildContext: z.string().describe('Free-text description of what the user wants to build'),
    },
    async ({ buildContext }) => {
      const { text, isError } = handleSaveBuildContext({ buildContext });
      return {
        content: [{ type: 'text' as const, text }],
        isError,
      };
    }
  );

  // -------------------------------------------------------------------------
  // 1. stackwright_pro_parse_otter_response
  // -------------------------------------------------------------------------
  server.tool(
    'stackwright_pro_parse_otter_response',
    "Parse and validate a specialist otter's QUESTION_COLLECTION_MODE JSON response. " +
      'Handles JSON extraction from LLM responses (strips markdown, fixes single quotes, trailing commas). ' +
      'Detects the phase from the otter name. ' +
      'Use this immediately after invoke_agent() to get a validated manifest phase object.',
    {
      otterName: z.string().describe('The agent name, e.g. "stackwright-pro-api-otter"'),
      responseText: z
        .string()
        .describe("Raw text response from the otter's QUESTION_COLLECTION_MODE invocation"),
    },
    async ({ otterName, responseText }) => {
      const { result, isError } = handleParseOtterResponse({ otterName, responseText });
      return {
        content: [{ type: 'text' as const, text: JSON.stringify(result) }],
        isError,
      };
    }
  );

  // -------------------------------------------------------------------------
  // 2. stackwright_pro_save_manifest
  // -------------------------------------------------------------------------
  server.tool(
    'stackwright_pro_save_manifest',
    'Write the question manifest to .stackwright/question-manifest.json. ' +
      'Call this after collecting and parsing questions from all otters via stackwright_pro_parse_otter_response.',
    {
      phases: jsonCoerce(
        z.array(
          z.object({
            phase: z.string(),
            otter: z.string(),
            questions: z.array(z.any()),
            requiredPackages: z
              .object({
                dependencies: z.record(z.string(), z.string()).optional(),
                devPackages: z.record(z.string(), z.string()).optional(),
              })
              .optional(),
          })
        )
      ).describe('Array of parsed phase objects from stackwright_pro_parse_otter_response'),
    },
    async ({ phases }) => {
      const { text, isError } = handleSaveManifest({ phases });
      return {
        content: [{ type: 'text' as const, text }],
        isError,
      };
    }
  );

  // -------------------------------------------------------------------------
  // 3. stackwright_pro_save_phase_answers
  // -------------------------------------------------------------------------
  server.tool(
    'stackwright_pro_save_phase_answers',
    'Save user answers for a phase to .stackwright/answers/{phase}.json. ' +
      'Pass rawAnswers directly from ask_user_question and the original manifest questions for label-to-value reverse mapping.',
    {
      phase: z.string().describe('Phase name, e.g. "designer"'),
      rawAnswers: jsonCoerce(
        z.array(
          z.object({
            question_header: z.string().min(1, 'question_header must not be empty'),
            selected_options: z
              .array(z.string())
              .min(1, 'selected_options must have at least one entry'),
            other_text: z.string().nullable().optional(),
          })
        )
      ).describe(
        'Answers as returned by ask_user_question — pass the native array, not a JSON string'
      ),
      questions: jsonCoerce(
        z
          .array(
            z
              .object({
                id: z.string(),
                question: z.string(),
                type: z.string(),
                options: z.array(z.object({ label: z.string(), value: z.string() })).optional(),
                required: z.boolean().optional(),
                default: z.union([z.string(), z.boolean(), z.array(z.string())]).optional(),
                help: z.string().optional(),
                dependsOn: z
                  .object({
                    questionId: z.string(),
                    value: z.union([z.string(), z.array(z.string())]),
                  })
                  .optional(),
              })
              .passthrough()
          )
          .optional()
      ).describe(
        'Original manifest questions for label→value reverse-mapping — pass the native array, not a JSON string'
      ),
    },
    async ({ phase, rawAnswers, questions }) => {
      const { text, isError } = handleSavePhaseAnswers({
        phase,
        rawAnswers,
        ...(questions && questions.length > 0
          ? { questions: questions as QuestionManifestQuestion[] }
          : {}),
      });
      return {
        content: [{ type: 'text' as const, text }],
        isError,
      };
    }
  );

  // -------------------------------------------------------------------------
  // 4. stackwright_pro_read_phase_answers
  // -------------------------------------------------------------------------
  server.tool(
    'stackwright_pro_read_phase_answers',
    'Read saved answers for a phase from .stackwright/answers/{phase}.json. ' +
      'Returns { missing: true } when no answers exist yet — use this to skip phases safely in the execution loop.',
    {
      phase: z.string().describe('Phase name, e.g. "designer"'),
    },
    async ({ phase }) => {
      const { text, isError } = handleReadPhaseAnswers({ phase });
      return {
        content: [{ type: 'text' as const, text }],
        isError,
      };
    }
  );

  // -------------------------------------------------------------------------
  // 5. stackwright_pro_get_otter_name
  // -------------------------------------------------------------------------
  server.tool(
    'stackwright_pro_get_otter_name',
    "Get the agent name for a phase (e.g. 'designer' → 'stackwright-pro-designer-otter'). " +
      'Use this in the execution loop to invoke the correct specialist otter without hardcoding names in the prompt.',
    {
      phase: z.string().describe('Phase name, e.g. "designer", "api", "pages"'),
    },
    async ({ phase }) => {
      const { text, isError } = handleGetOtterName({ phase });
      return {
        content: [{ type: 'text' as const, text }],
        isError,
      };
    }
  );
}
